import React, { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { IconFillStyles, Spinner, WarningIcon } from './Iconset';
import Dialog from './layout/Dialog';
import Panel from './layout/Panel';
import PlaceholderContent from './layout/PlaceholderContent';
import Spacer from './layout/Spacer';
import BoolProperty from './properties/BoolProperty';
import Button from './layout/Button';
import SearchField from './layout/SearchField';
import DropdownProperty from './properties/DropdownProperty';
import { useTypedSelector } from '../state/hooks';
import Scenario from '../classes/scenarioElements/Scenario';
import { FeatureFlagNames, LicenseFilters } from '../interfaces/enums';
import { LayoutOrientations } from '../globals';
import SessionUtils from '../classes/utilities/SessionUtils';
import GQLUser from '../classes/api/GQL/user/GQLuser';
import { LicenseTypeEnum, User } from '../classes/cms-glue/gql';
import GQLprojects from '../classes/api/GQL/projects/GQLprojects';

interface ScenarioShareDialogProps
{
	projectID?: string;
	title?: string;
	review?: boolean;
	hideSelectAll?: boolean;
	infoMessage?: string;
	onShutdown?: () => void;
	onConfirmed?: () => void;
}

export default function ScenarioShareDialog(props: ScenarioShareDialogProps)
{
	const sessionInfo = useTypedSelector(state => state.userModule.sessionInfo, shallowEqual);
	const [licenseFilter, setLicenseFilter] = useState<LicenseFilters>(LicenseFilters.All);
	const [authors, setAuthors] = useState<User[]>([]);
	const [filteredAuthors, setFilteredAuthors] = useState<Array<any>>([]);
	const [error, setError] = useState<string>("");
	const [userIDs, setUserIDs] = useState<Array<string>>([]);
	const [searchText, setSearchText] = useState<string>("");
	const projectID: string = props.projectID || Scenario.current()?.id || "";
	const viewOnlyMode = SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.viewOnlyMode);

	if(!sessionInfo || !projectID) return null;

	useEffect(() =>
	{
		// to fix react error, as per https://stackoverflow.com/a/53572588/618726
		async function fetchAuthors()
		{
			try
			{
				const users = await GQLUser.getAuthors(viewOnlyMode)
				
				console.log(users);
				// first we filter to only users that even have access to worklink
				// note that "WORKLINK" and "ACTIVE" are defined in LicenseProductEnum in services/common/utils/gql.ts
				let worklinkUsers = users;

				if(viewOnlyMode)
				{
					worklinkUsers = worklinkUsers.filter(user => user.licenses?.nodes ? user.licenses.nodes.some(license => license?.product === "WORKLINK" && license?.state === "ACTIVE") : true);
				}
				// then we filter out the current user
				const otherUsers = worklinkUsers.filter(user => Number(user.databaseId) !== Number(sessionInfo?.cmsUserId));
				//console.log(otherUsers);
				if (!otherUsers.length)
				{
					setError("There are no other authors to share this project with...");
				}
				else
				{
					otherUsers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
				}
				// Convert CmsUser to User type by adding required NodeInterface properties
				setAuthors(otherUsers.map(user=> ({
					...user,
					dominantLicense: SessionUtils.getAuthorLicense(user),
					path: '',
					settings: {},
					url: '',
					id: user.databaseId || "" // Ensure id is included
				})));
			}
			catch(error)
			{
				console.error(error);
				// Type check error before accessing .message
				const errorMessage = error instanceof Error ? error.message : String(error);
				setError("Fetching authors list failed: " + errorMessage);
			}
		}

		async function fetchWhitelist()
		{
			try 
			{
				const data = await GQLUser.getAuthorizedUsers(projectID)
				setUserIDs(data.whitelisted?.filter((id): id is string => typeof id === 'string' && id !== '') ?? []);
			}
			catch (error) 
			{
				console.error(error);
				// Type check error before accessing .message
				const errorMessage = error instanceof Error ? error.message : String(error);
				setError("Fetching authorized list failed: " + errorMessage);
			}
		}

		fetchWhitelist().then(fetchAuthors);
	}, []);

	useEffect(() =>
	{
		filterAuthors();
	}, [authors, searchText, licenseFilter]);

	function isAuthorized(authorId)
	{
		for (const id of userIDs)
		{
			if (authorId == id)
				return true;
		}

		return false;
	}

	function filterAuthors()
	{
		//remove selection from hidden authors? (uncomment to enable)
		// const hiddenAuthors = authors.filter(author => !author.name.toLowerCase().includes(searchText.toLowerCase()));
		// const hiddenUserIds = hiddenAuthors.map(author => author.id);
		// setUserIDs(ids => ids.filter(id => !hiddenUserIds.includes(id)));
		
		let authorsToShow = [...authors];

		// filter by search text
		if(searchText)
		{
			authorsToShow = authorsToShow.filter(author => author.name?.toLowerCase().includes(searchText.toLowerCase()));
		}

		// filter by license type
		if(viewOnlyMode)
		{
			switch(licenseFilter)
			{
				case LicenseFilters.Authors:
					authorsToShow = authorsToShow.filter(author => author.licenses?.nodes ? author.licenses.nodes.some(license => license?.type === LicenseTypeEnum.Author) : false);
					break;
				case LicenseFilters.Viewers:
					authorsToShow = authorsToShow.filter(author => author.licenses?.nodes ? author.licenses.nodes.some(license => license?.type === LicenseTypeEnum.Viewer) : false);
					break;
			}
		}
		else
		{
			authorsToShow = authorsToShow.filter(author => author.licenses?.nodes ? !author.licenses.nodes.every(license => license?.type === LicenseTypeEnum.Viewer) : false);
		}

		setFilteredAuthors(authorsToShow);
	}


	function selectAll()
	{
		const IDs = filteredAuthors.map(author => author.id);
		setUserIDs(IDs);
	}

	function deSelectAll()
	{
		setUserIDs([]);
	}

	return (
		<Dialog title={props.review ? "Share Project For Review" :  "Share Project"}
			data-name={"scenario-share-dialog-root"}
			onShutdown={props.onShutdown}
			actions={[
				{
					label: "Share",
					onClick: () =>
					{
						// we can't actually send a completely empty array to the API, so we need to make sure the current user's id is there
						if(!userIDs.length)
						{
							userIDs.push(sessionInfo.cmsUserId);
						}

						GQLprojects.updateProjectWhitelist(userIDs, projectID);
						if(props.onConfirmed)
						{
							props.onConfirmed();
						}
					},
				},
				{
					label: "Cancel",
				}
			]}
		>
			{
				error ?
					<PlaceholderContent>
						<div>
							{error}
						</div>
					</PlaceholderContent>
					:
					!authors.length ?
						<PlaceholderContent>
							<Spinner size={60} />
							<Spacer size={{ y: "12px" }} />
							<div>
								Fetching users...
							</div>
						</PlaceholderContent>
						:
						<div className={"shareDialogContent"}>
							<SearchField
								data-name={'user-share-search'}
								value={searchText}
								placeholder={props.review ? "Search for a user" : ""}
								onChangeCallback={(v) => { setSearchText(v) }}
							/>
							<Spacer size={{ y: "10px" }} />
							<div className={"shareDialogUsersHeader"}>
								{props.review ? <div>Select a user to review</div> :  <div>Share Project with</div>}
								<Spacer stretchTo={{ x: "100%" }} />
								{
									SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.viewOnlyMode) &&
										<DropdownProperty data-name={"license-filter"}
											className={"fit"}
											value={licenseFilter}
											options={[
												{ display: "All users", value: LicenseFilters.All },
												{ display: "Authors", value: LicenseFilters.Authors },
												{ display: "Viewers", value: LicenseFilters.Viewers },
											]}
											onCommitCallback={setLicenseFilter}
										/>
								}
							</div>
							<Spacer size={{ y: "4px" }} />
							<Panel className={"solid recipientListRoot"}>
								{
									!filteredAuthors.length ? "There are no other users..." :
										filteredAuthors.map((author, idx) =>
										{
											return (
												<div key={idx}
													className={"authorRow"}
												>
													<BoolProperty className={""}
														data-name={"recipient-toggle-" + author.name}
														labelAfterProperty={author.name}
														label={''}
														key={idx}
														value={isAuthorized(author.id)}
														onChangeCallback={(v) =>
														{
															if (v)
															{
																setUserIDs(ids => ids.concat(author.id));
															}
															else
															{
																setUserIDs(ids => ids.filter(id => id != author.id));
															}
														}}
													/>
													{
														SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.viewOnlyMode) &&
															<div>{author.dominantLicense}</div>
													}
												</div>
											);
										})
								}
							</Panel>
							{
								!props.hideSelectAll && 
									<div>
										<Spacer size={{ y: "8px" }} />
										<Button 
											data-name={"recipient-list-toggle-select-all"}
											label={userIDs.length ? "Deselect All" : "Select All"}
											onClick={() => { userIDs.length ? deSelectAll() : selectAll()}}
										/> 
									</div>
							}
							{
								props.infoMessage && 
									<div>
										<Spacer size={{ y: "24px" }} />
										<div > 
											<Panel className={"centerContentVertically"} orientation={LayoutOrientations.Horizontal}>
												<WarningIcon fillStyle={IconFillStyles.yellow} />
												<Spacer size={{ x: "6px" }} />
												{props.infoMessage}
											</Panel>							
										</div>
									</div>
							}
						</div>
			}
		</Dialog>
	);
}
